import { commercialReference } from './quoteReference';
import { assertEuro } from './quoteFinancialInput';
import { assertQuoteDocumentReady } from './quoteDocumentReadiness';
import { assertFinancialPresentation, taxBreakdownHtml, optionFinancialHtml } from './quoteFinancialPresentation';
import {
  DOCUMENT_COMPOSITIONS,
  buildHtmlDocumentShell,
  companyDocumentIdentity,
  openHtmlDocument,
} from '../../shared/documents';

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const decimal = (value, digits = 2) => {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric)
    ? numeric.toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits })
    : '-';
};

const percent = (value) => `${decimal(Number(value || 0) * 100, 2)} %`;
const money = (value, formatMoney) => formatMoney ? formatMoney(value || 0) : `${decimal(value, 2)} €`;

const natureLabel = (nature) => ({
  labor: 'Main-d’œuvre',
  material: 'Matériel',
  subcontracting: 'Sous-traitance',
  co_contracting: 'Co-traitance',
  internal_study: 'Études internes',
  external_study: 'Études externes',
  other: 'Autres coûts directs',
}[nature] || 'Autres coûts');

const allocationLabel = (method) => ({
  by_amount: 'Prorata du montant',
  by_labor_hours: 'Prorata des heures de main-d’œuvre',
  by_labor_cost: 'Prorata du coût de main-d’œuvre',
  by_material_cost: 'Prorata du coût matériel',
  by_quantity: 'Prorata des quantités',
  by_weight: 'Selon pondération',
  manual: 'Répartition manuelle',
  fixed: 'Affectation fixe',
}[method] || 'Répartition');

const commonStyle = `
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { margin: 0; color: #202124; font-family: Arial, Helvetica, sans-serif; font-size: 10.5px; line-height: 1.35; }
  .doc-header { border-bottom: 3px solid #c9003d; padding-bottom: 10px; margin-bottom: 16px; display:flex; justify-content:space-between; gap:20px; align-items:flex-start; }
  .doc-header .brand { font-weight: 800; font-size: 15px; color:#c9003d; }
  .doc-header h1 { margin: 3px 0 0; font-size: 22px; }
  .doc-header .meta { text-align:right; color:#5f6368; }
  .doc-intro { display:grid; grid-template-columns:1fr 1fr; gap:8px 20px; background:#f7f8fa; border:1px solid #dde2e7; padding:10px 12px; margin-bottom:16px; }
  .doc-intro strong { display:block; color:#5f6368; font-size:8px; text-transform:uppercase; letter-spacing:.04em; }
  h2 { font-size:14px; margin:18px 0 8px; padding-bottom:5px; border-bottom:1px solid #d8dde3; }
  h3 { font-size:11.5px; margin:12px 0 6px; }
  table { width:100%; border-collapse:collapse; margin: 6px 0 12px; }
  th { background:#292f36; color:white; text-align:left; font-size:8px; text-transform:uppercase; letter-spacing:.03em; }
  th, td { border-bottom:1px solid #dde2e7; padding:6px 7px; vertical-align:top; }
  .right { text-align:right; white-space:nowrap; }
  .center { text-align:center; }
  .section { margin:10px 0; break-inside:avoid; }
  .section-title { background:#eef1f4; border-left:4px solid #c9003d; padding:7px 9px; font-weight:700; }
  .subsection-title { margin:8px 0 4px; font-weight:700; color:#343a40; }
  .totals { margin-left:auto; width:52%; border:1px solid #d8dde3; padding:8px 12px; }
  .totals div { display:flex; justify-content:space-between; gap:16px; padding:3px 0; }
  .totals .grand { border-top:2px solid #292f36; margin-top:5px; padding-top:7px; font-weight:800; font-size:12px; }
  .cards { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .card { border:1px solid #d8dde3; padding:10px; break-inside:avoid; }
  .card h3 { margin-top:0; }
  .card dl { margin:0; }
  .card dl div { display:flex; justify-content:space-between; gap:12px; padding:3px 0; border-bottom:1px solid #edf0f2; }
  .muted { color:#6b7280; }
  .note { background:#fff8e8; border-left:4px solid #e6a700; padding:8px 10px; margin:10px 0; }
  .page-break { break-before:page; }
  @media print { body { -webkit-print-color-adjust:exact; print-color-adjust:exact; } }
`;

const header = (editor, title) => `
  <header class="doc-header">
    <div><div class="brand">${escapeHtml(companyDocumentIdentity.legalName || companyDocumentIdentity.displayName)}</div><h1>${escapeHtml(title)}</h1></div>
    <div class="meta"><strong>${escapeHtml(commercialReference(editor.quoteNumber, editor.versionNumber ?? 1) || 'Devis')}</strong><br/>Version ${escapeHtml(editor.versionNumber || 1)}</div>
  </header>
  <section class="doc-intro">
    <div><strong>Devis</strong>${escapeHtml(commercialReference(editor.quoteNumber, editor.versionNumber ?? 1) || '-')}</div>
    <div><strong>Titre</strong>${escapeHtml(editor.title || '-')}</div>
    <div><strong>Affaire</strong>${escapeHtml(editor.affairLabel || editor.affairCode || editor.affairId || 'Sans affaire')}</div>
    <div><strong>Projet</strong>${escapeHtml(editor.projectLabel || editor.projectCode || editor.projectId || 'Sans projet')}</div>
  </section>`;

const minuteLine = (line, formatMoney) => `
  <tr>
    <td><strong>${escapeHtml(line.label || 'Ligne')}</strong></td>
    <td class="right">${decimal(line.quantity, 2)} ${escapeHtml(line.unit || '')}</td>
    <td class="right">${money(line.direct_cost, formatMoney)}</td>
    <td class="right">${money(line.imputed_charge, formatMoney)}</td>
    <td class="right">${money(line.allocated_overhead, formatMoney)}</td>
    <td class="right">${money(line.cost_price, formatMoney)}</td>
    <td class="right">${money(line.net_selling_price_ht, formatMoney)}</td>
  </tr>`;

const minuteChapter = (chapter, formatMoney, depth = 0) => `
  <section class="section">
    <div class="${depth ? 'subsection-title' : 'section-title'}">${escapeHtml(chapter.title || 'Ensemble')} <span class="muted">- HT ${money(chapter.net_selling_price_ht, formatMoney)}</span></div>
    ${(chapter.lines || []).length ? `<table><thead><tr><th>Désignation</th><th class="right">Qté</th><th class="right">Déboursé</th><th class="right">Charges</th><th class="right">Frais ventilés</th><th class="right">PR</th><th class="right">PV HT</th></tr></thead><tbody>${(chapter.lines || []).map((line) => minuteLine(line, formatMoney)).join('')}</tbody></table>` : ''}
    ${(chapter.chapters || []).map((child) => minuteChapter(child, formatMoney, depth + 1)).join('')}
  </section>`;

const buildMinuteHtml = ({ editor, calculated, formatMoney }) => {
  const minute = calculated?.minute || {};
  const calculation = calculated?.calculation || {};
  return buildHtmlDocumentShell({ title: `Minute - ${commercialReference(editor.quoteNumber, editor.versionNumber ?? 1) || ''}`, composition: DOCUMENT_COMPOSITIONS.INTERNAL, styles: commonStyle, body: `
    ${header(editor, 'Minute de chiffrage')}
    <p class="muted">Décomposition interne des coûts, charges, prix de revient et prix de vente.</p>
    ${(minute.chapters || []).map((chapter) => minuteChapter(chapter, formatMoney)).join('') || '<p>Aucune ligne chiffrée.</p>'}
    ${taxBreakdownHtml(calculated, formatMoney)}
    <h2>Totaux</h2>
    <div class="totals">
      <div><span>Déboursé direct</span><strong>${money(calculation.total_direct_cost, formatMoney)}</strong></div>
      <div><span>Frais directs</span><strong>${money(calculation.total_direct_expenses, formatMoney)}</strong></div>
      <div><span>Charges imputées</span><strong>${money(calculation.total_imputed_charges, formatMoney)}</strong></div>
      <div><span>Frais ventilés</span><strong>${money(calculation.total_allocated_overhead, formatMoney)}</strong></div>
      <div><span>Prix de revient</span><strong>${money(calculation.total_cost_price, formatMoney)}</strong></div>
      <div><span>Remise</span><strong>${money(calculation.total_discount, formatMoney)}</strong></div>
      <div class="grand"><span>Prix de vente net HT</span><strong>${money(calculation.total_net_ht, formatMoney)}</strong></div>
      <div><span>TVA</span><strong>${money(calculation.total_tax, formatMoney)}</strong></div>
      <div><span>Total TTC</span><strong>${money(calculation.total_ttc, formatMoney)}</strong></div>
    </div>
    ${optionFinancialHtml(calculated, formatMoney)}
  ` });
};

const rows = (items, labeler, formatMoney) => (items || []).map((item) => `<tr><td>${escapeHtml(labeler(item))}</td><td class="right">${money(item.amount, formatMoney)}</td></tr>`).join('');

const buildCalculationHtml = ({ editor, calculated, formatMoney }) => {
  const sheet = calculated?.salesSheet || {};
  return buildHtmlDocumentShell({ title: `Méthodes de calcul - ${commercialReference(editor.quoteNumber, editor.versionNumber ?? 1) || ''}`, composition: DOCUMENT_COMPOSITIONS.INTERNAL, styles: commonStyle, body: `
    ${header(editor, 'Méthodes de calcul')}
    <div class="note">Ce document décrit la formation économique de la version courante du devis. Les valeurs affichées proviennent du calcul enregistré pour cette version.</div>
    <div class="cards">
      <article class="card"><h3>Formation du prix de revient</h3><dl>
        <div><dt>Déboursé direct</dt><dd>${money(sheet.direct_cost, formatMoney)}</dd></div>
        <div><dt>Frais directs</dt><dd>${money(sheet.direct_expenses, formatMoney)}</dd></div>
        <div><dt>Charges imputées</dt><dd>${money(sheet.imputed_charges, formatMoney)}</dd></div>
        <div><dt>Frais ventilés</dt><dd>${money(sheet.allocated_overhead, formatMoney)}</dd></div>
        <div><dt><strong>Prix de revient</strong></dt><dd><strong>${money(sheet.cost_price, formatMoney)}</strong></dd></div>
      </dl></article>
      <article class="card"><h3>Formation du prix de vente</h3><dl>
        <div><dt>PV de base</dt><dd>${money(sheet.base_selling_price_ht, formatMoney)}</dd></div>
        <div><dt>Frais en % du PV</dt><dd>${money(sheet.selling_price_expenses, formatMoney)}</dd></div>
        <div><dt>PV brut</dt><dd>${money(sheet.gross_selling_price_ht, formatMoney)}</dd></div>
        <div><dt>Remise</dt><dd>${money(sheet.discount, formatMoney)}</dd></div>
        <div><dt><strong>PV net HT</strong></dt><dd><strong>${money(sheet.net_selling_price_ht, formatMoney)}</strong></dd></div>
      </dl></article>
    </div>
    <h2>Charges imputées</h2>
    <table><thead><tr><th>Nature</th><th class="right">Montant</th></tr></thead><tbody>${rows(sheet.imputed_charge_breakdown, (item) => natureLabel(item.nature), formatMoney) || '<tr><td colspan="2">Aucune charge imputée.</td></tr>'}</tbody></table>
    <h2>Frais ventilés</h2>
    <table><thead><tr><th>Frais / méthode</th><th class="right">Montant</th></tr></thead><tbody>${rows(sheet.overhead_breakdown, (item) => `${item.label || 'Frais'} - ${allocationLabel(item.allocation_method)}`, formatMoney) || '<tr><td colspan="2">Aucun frais ventilé.</td></tr>'}</tbody></table>
    <h2>Frais calculés sur le prix de vente</h2>
    <table><thead><tr><th>Frais</th><th class="right">Taux</th><th class="right">Montant</th></tr></thead><tbody>${(sheet.selling_price_expense_breakdown || []).map((item) => `<tr><td>${escapeHtml(item.label || 'Frais')}</td><td class="right">${percent(item.rate)}</td><td class="right">${money(item.amount, formatMoney)}</td></tr>`).join('') || '<tr><td colspan="3">Aucun frais sur prix de vente.</td></tr>'}</tbody></table>
    ${taxBreakdownHtml(calculated, formatMoney)}
    <h2>Indicateurs économiques</h2>
    <div class="cards">
      <article class="card"><dl><div><dt>Résultat</dt><dd>${money(sheet.result, formatMoney)}</dd></div><div><dt>Taux de marge</dt><dd>${percent(sheet.margin_rate)}</dd></div><div><dt>Taux de marque</dt><dd>${percent(sheet.markup_rate)}</dd></div></dl></article>
      <article class="card"><dl><div><dt>PV / PR</dt><dd>${decimal(sheet.pv_pr_ratio, 2)}</dd></div><div><dt>TVA</dt><dd>${money(sheet.tax, formatMoney)}</dd></div><div><dt>Total TTC</dt><dd>${money(sheet.total_ttc, formatMoney)}</dd></div></dl></article>
    </div>
    ${optionFinancialHtml(calculated, formatMoney)}
  ` });
};

export const buildInternalQuoteHtml = (context, documentType) => {
  assertEuro(context.editor.currencyCode);
  if (context.calculated?.salesSheet?.currency !== undefined) assertEuro(context.calculated.salesSheet.currency);
  const totals = assertFinancialPresentation(context.calculated,
    context.editor.versionStatus && context.editor.versionStatus !== 'draft' ? context.editor.serverCalculation?.totals : null);
  if (context.calculated.source === 'recorded') {
    return buildHtmlDocumentShell({ title: 'Montants enregistrés', composition: DOCUMENT_COMPOSITIONS.INTERNAL,
      styles: commonStyle, body: `${header(context.editor, 'Montants enregistrés')}${taxBreakdownHtml(context.calculated, context.formatMoney)}
      <p>Total HT : ${money(totals.totalHT, context.formatMoney)}</p><p>TVA : ${money(totals.totalVat, context.formatMoney)}</p><p>Total TTC : ${money(totals.totalTTC, context.formatMoney)}</p>${optionFinancialHtml(context.calculated, context.formatMoney)}` });
  }
  return documentType === 'minute' ? buildMinuteHtml(context) : buildCalculationHtml(context);
};

const open = async (context, documentType, print = false) => {
  assertQuoteDocumentReady({ ...context, documentType });
  if (!['minute', 'calculation_detail'].includes(documentType)) throw new Error('Type de document interne non autorisé.');
  const permission = documentType === 'minute' ? 'canViewMinute' : 'canViewCosts';
  if (!context.editor.permissions?.[permission]) throw new Error('Accès à ce document interne refusé.');
  return openHtmlDocument({
      html: buildInternalQuoteHtml(context, documentType),
      print,
  });
};

export const previewInternalQuotePdf = (context, documentType) => open(context, documentType, false);
export const printInternalQuotePdf = (context, documentType) => open(context, documentType, true);
